import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, ArrowLeft, Target, CheckCircle2, Lightbulb, BookOpen,
  Briefcase, ClipboardCheck, FileText, BarChart3, ChevronRight,
  Play, Lock, Sparkles, Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import LabDashboard from "./LabDashboard";
import AIToolsConsole, { AIToolsSidebar } from "./AIToolsConsole";

/**
 * LabTemplate - Reusable template for all ProLab lab pages
 * Contains all 8 required sections with modular, reusable components
 */
export default function LabTemplate({
  // Lab metadata
  labId,
  labName,
  labSubtitle,
  labDescription,
  labColor = "#1E3A8A",
  labIcon: LabIcon,
  category,
  
  // Content data
  overview,
  missions = [],
  aiTools = [],
  guidedPractice = [],
  realWorldApplications = [],
  assessments = [],
  portfolioOutputs = [],
  
  // Progress data (would come from auth/backend in production)
  completedMissions = [],
  userOutputs = [],
  readinessScore = 0,
  recommendedLabs = []
}) {
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedMission, setExpandedMission] = useState(null);

  const progressPercent = missions.length > 0 
    ? (completedMissions.length / missions.length) * 100 
    : 0;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid={`lab-page-${labId}`}>
      {/* Hero Section */}
      <section style={{ backgroundColor: labColor }} className="text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link 
              to="/juswork/prolab" 
              className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-4 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to ProLab
            </Link>
            
            <div className="flex items-start gap-6">
              <div className="hidden md:flex w-20 h-20 rounded-2xl bg-white/10 items-center justify-center flex-shrink-0">
                {LabIcon && <LabIcon className="w-10 h-10 text-white" />}
              </div>
              
              <div className="flex-1">
                <Badge variant="outline" className="mb-3 border-white/30 text-white text-xs">
                  {category}
                </Badge>
                <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2">
                  {labName}
                </h1>
                <p className="text-lg text-white/80 mb-4">{labSubtitle}</p>
                
                <div className="flex flex-wrap items-center gap-4 text-sm text-white/70">
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    <span>{missions.length} Missions</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    <span>{aiTools.length} AI Tools</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    <span>{portfolioOutputs.length} Outputs</span>
                  </div>
                </div>
              </div>
              
              {/* Progress Ring */}
              <div className="hidden lg:flex flex-col items-center">
                <div className="relative w-24 h-24">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="48" cy="48" r="42"
                      stroke="rgba(255,255,255,0.2)"
                      strokeWidth="8"
                      fill="none"
                    />
                    <circle
                      cx="48" cy="48" r="42"
                      stroke="white"
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${(progressPercent / 100) * 264} 264`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-mono text-xl font-bold">{Math.round(progressPercent)}%</span>
                  </div>
                </div>
                <span className="text-xs text-white/60 mt-2">Progress</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Main Column */}
          <div className="lg:col-span-8">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
              <TabsList className="bg-white border border-[#E5E7EB] p-1 h-auto flex-wrap">
                <TabsTrigger value="overview" className="gap-2 data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-white">
                  <BookOpen className="w-4 h-4" /> Overview
                </TabsTrigger>
                <TabsTrigger value="missions" className="gap-2 data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-white">
                  <Target className="w-4 h-4" /> Missions
                </TabsTrigger>
                <TabsTrigger value="ai-toolkit" className="gap-2 data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-white">
                  <Sparkles className="w-4 h-4" /> AI Toolkit
                </TabsTrigger>
                <TabsTrigger value="practice" className="gap-2 data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-white">
                  <Play className="w-4 h-4" /> Practice
                </TabsTrigger>
                <TabsTrigger value="assessment" className="gap-2 data-[state=active]:bg-[#1E3A8A] data-[state=active]:text-white">
                  <ClipboardCheck className="w-4 h-4" /> Assessment
                </TabsTrigger>
              </TabsList>

              {/* 1. Lab Overview */}
              <TabsContent value="overview">
                <LabOverviewSection 
                  overview={overview}
                  labColor={labColor}
                />
              </TabsContent>

              {/* 2. Missions */}
              <TabsContent value="missions">
                <MissionsSection 
                  missions={missions}
                  completedMissions={completedMissions}
                  expandedMission={expandedMission}
                  onExpandMission={setExpandedMission}
                  labColor={labColor}
                />
              </TabsContent>

              {/* 3. AI Toolkit */}
              <TabsContent value="ai-toolkit">
                <Card className="border-[#E5E7EB] bg-white">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl" style={{ color: '#991B1B' }}>
                      AI Toolkit
                    </CardTitle>
                    <p className="text-sm text-[#1E3A8A]/70">
                      AI-powered tools and assistants to help you complete lab missions.
                    </p>
                  </CardHeader>
                  <CardContent>
                    <AIToolsConsole 
                      labId={labId}
                      availableTools={aiTools}
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* 4. Guided Practice + 5. Real World Application */}
              <TabsContent value="practice">
                <div className="space-y-6">
                  <GuidedPracticeSection 
                    exercises={guidedPractice}
                    labColor={labColor}
                  />
                  <RealWorldApplicationSection 
                    applications={realWorldApplications}
                    labColor={labColor}
                  />
                </div>
              </TabsContent>

              {/* 6. Assessment */}
              <TabsContent value="assessment">
                <AssessmentSection 
                  assessments={assessments}
                  labColor={labColor}
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            {/* 8. Lab Intelligence Dashboard */}
            <LabDashboard 
              labId={labId}
              labName={labName}
              missions={missions}
              completedMissions={completedMissions}
              outputs={userOutputs}
              readinessScore={readinessScore}
              recommendedLabs={recommendedLabs}
              showFullDashboard={false}
            />

            {/* 7. Portfolio Outputs */}
            <PortfolioOutputsCard 
              outputs={portfolioOutputs}
              labColor={labColor}
            />

            {/* Quick AI Tools */}
            <Card className="border-[#E5E7EB] bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-base" style={{ color: '#991B1B' }}>
                  Quick AI Tools
                </CardTitle>
              </CardHeader>
              <CardContent>
                <AIToolsSidebar tools={aiTools} />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// REUSABLE SECTION COMPONENTS
// ============================================

/**
 * Lab Overview Section
 */
function LabOverviewSection({ overview, labColor }) {
  return (
    <Card className="border-[#E5E7EB] bg-white">
      <CardContent className="p-6 space-y-6">
        <div>
          <h3 className="font-heading text-lg font-semibold mb-3" style={{ color: '#991B1B' }}>
            Lab Purpose
          </h3>
          <p className="text-[#1E3A8A] leading-relaxed">{overview?.purpose}</p>
        </div>
        
        <div>
          <h3 className="font-heading text-lg font-semibold mb-3" style={{ color: '#991B1B' }}>
            Real-World Applications
          </h3>
          <ul className="space-y-2">
            {overview?.applications?.map((app, i) => (
              <li key={i} className="flex items-start gap-2 text-[#1E3A8A]">
                <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: labColor }} />
                <span>{app}</span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-heading text-lg font-semibold mb-3" style={{ color: '#991B1B' }}>
            Skills Developed
          </h3>
          <div className="flex flex-wrap gap-2">
            {overview?.skills?.map((skill, i) => (
              <Badge key={i} variant="outline" className="bg-[#F8FAFC] text-[#1E3A8A] border-[#E5E7EB]">
                {skill}
              </Badge>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Missions Section - Reusable mission cards
 */
function MissionsSection({ missions, completedMissions, expandedMission, onExpandMission, labColor }) {
  return (
    <div className="space-y-4">
      {missions.map((mission, index) => (
        <MissionCard 
          key={mission.id}
          mission={mission}
          index={index}
          isCompleted={completedMissions.includes(mission.id)}
          isExpanded={expandedMission === mission.id}
          onToggle={() => onExpandMission(expandedMission === mission.id ? null : mission.id)}
          labColor={labColor}
        />
      ))}
    </div>
  );
}

/**
 * Reusable Mission Card
 */
export function MissionCard({ mission, index, isCompleted, isExpanded, onToggle, labColor }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <Card className={`border-[#E5E7EB] bg-white overflow-hidden ${isCompleted ? 'border-l-4' : ''}`}
            style={isCompleted ? { borderLeftColor: labColor } : {}}>
        <CardContent className="p-0">
          {/* Header */}
          <button
            onClick={onToggle}
            className="w-full p-4 flex items-center gap-4 text-left hover:bg-[#F8FAFC] transition-colors"
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              isCompleted ? 'text-white' : 'bg-[#E5E7EB] text-[#1E3A8A]'
            }`} style={isCompleted ? { backgroundColor: labColor } : {}}>
              {isCompleted ? (
                <CheckCircle2 className="w-5 h-5" />
              ) : (
                <span className="font-mono font-semibold">{index + 1}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-heading font-semibold text-[#111827]">{mission.title}</h4>
              <p className="text-sm text-[#1E3A8A]/60 truncate">{mission.objective}</p>
            </div>
            <ChevronRight className={`w-5 h-5 text-[#1E3A8A]/40 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
          </button>

          {/* Expanded Content */}
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-t border-[#E5E7EB] p-4 bg-[#F8FAFC] space-y-4"
            >
              <div>
                <h5 className="text-sm font-semibold text-[#991B1B] mb-2">Objective</h5>
                <p className="text-sm text-[#1E3A8A]">{mission.objective}</p>
              </div>
              
              {mission.steps && (
                <div>
                  <h5 className="text-sm font-semibold text-[#991B1B] mb-2">Steps</h5>
                  <ol className="space-y-2">
                    {mission.steps.map((step, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#1E3A8A]">
                        <span className="font-mono text-xs bg-[#1E3A8A]/10 px-1.5 py-0.5 rounded">{i + 1}</span>
                        {step}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {mission.aiPrompts && (
                <div>
                  <h5 className="text-sm font-semibold text-[#991B1B] mb-2">Suggested AI Prompts</h5>
                  <div className="space-y-2">
                    {mission.aiPrompts.map((prompt, i) => (
                      <div key={i} className="p-2 bg-white rounded border border-[#E5E7EB] text-xs text-[#1E3A8A] font-mono">
                        {prompt}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {mission.expectedOutput && (
                <div>
                  <h5 className="text-sm font-semibold text-[#991B1B] mb-2">Expected Output</h5>
                  <p className="text-sm text-[#1E3A8A]">{mission.expectedOutput}</p>
                </div>
              )}

              {mission.checklist && (
                <div>
                  <h5 className="text-sm font-semibold text-[#991B1B] mb-2">Completion Checklist</h5>
                  <div className="space-y-1">
                    {mission.checklist.map((item, i) => (
                      <label key={i} className="flex items-center gap-2 text-sm text-[#1E3A8A] cursor-pointer">
                        <input type="checkbox" className="rounded border-[#E5E7EB]" />
                        {item}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <Button className="w-full text-white" style={{ backgroundColor: labColor }}>
                {isCompleted ? 'Review Mission' : 'Start Mission'}
              </Button>
            </motion.div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

/**
 * Guided Practice Section
 */
function GuidedPracticeSection({ exercises, labColor }) {
  return (
    <Card className="border-[#E5E7EB] bg-white">
      <CardHeader>
        <CardTitle className="font-heading text-xl" style={{ color: '#991B1B' }}>
          Guided Practice
        </CardTitle>
        <p className="text-sm text-[#1E3A8A]/70">
          Scenario-based exercises to build practical skills.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {exercises.map((exercise, index) => (
          <ExerciseCard key={index} exercise={exercise} index={index} labColor={labColor} />
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * Reusable Exercise Card
 */
export function ExerciseCard({ exercise, index, labColor }) {
  return (
    <div className="p-4 rounded-lg border border-[#E5E7EB] bg-[#F8FAFC]">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
             style={{ backgroundColor: labColor }}>
          {index + 1}
        </div>
        <div>
          <h4 className="font-semibold text-[#111827] mb-1">{exercise.title}</h4>
          <p className="text-sm text-[#1E3A8A]/70 mb-3">{exercise.scenario}</p>
          <Button size="sm" variant="outline" className="border-[#E5E7EB]">
            <Play className="w-4 h-4 mr-1" /> Start Exercise
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Real World Application Section
 */
function RealWorldApplicationSection({ applications, labColor }) {
  return (
    <Card className="border-[#E5E7EB] bg-white">
      <CardHeader>
        <CardTitle className="font-heading text-xl" style={{ color: '#991B1B' }}>
          Real-World Application
        </CardTitle>
        <p className="text-sm text-[#1E3A8A]/70">
          Apply your skills to actual work projects and environments.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {applications.map((app, index) => (
          <div key={index} className="flex items-start gap-3 p-3 rounded-lg border border-[#E5E7EB]">
            <Briefcase className="w-5 h-5 flex-shrink-0" style={{ color: labColor }} />
            <div>
              <h4 className="font-semibold text-[#111827] text-sm">{app.title}</h4>
              <p className="text-xs text-[#1E3A8A]/70">{app.description}</p>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * Assessment Section
 */
function AssessmentSection({ assessments, labColor }) {
  return (
    <Card className="border-[#E5E7EB] bg-white">
      <CardHeader>
        <CardTitle className="font-heading text-xl" style={{ color: '#991B1B' }}>
          Assessment
        </CardTitle>
        <p className="text-sm text-[#1E3A8A]/70">
          Knowledge checks, scenario questions, and readiness assessment.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {assessments.map((assessment, index) => (
          <AssessmentCard key={index} assessment={assessment} index={index} labColor={labColor} />
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * Reusable Assessment Card
 */
export function AssessmentCard({ assessment, index, labColor }) {
  return (
    <div className="p-4 rounded-lg border border-[#E5E7EB]">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-[#111827]">{assessment.title}</h4>
        <Badge variant="outline" style={{ borderColor: `${labColor}40`, color: labColor }}>
          {assessment.type}
        </Badge>
      </div>
      <p className="text-sm text-[#1E3A8A]/70 mb-3">{assessment.description}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#1E3A8A]/50">{assessment.questions} questions</span>
        <Button size="sm" className="text-white" style={{ backgroundColor: labColor }}>
          <ClipboardCheck className="w-4 h-4 mr-1" /> Take Assessment
        </Button>
      </div>
    </div>
  );
}

/**
 * Portfolio Outputs Card
 */
function PortfolioOutputsCard({ outputs, labColor }) {
  return (
    <Card className="border-[#E5E7EB] bg-white">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-base" style={{ color: '#991B1B' }}>
          Portfolio Outputs
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {outputs.map((output, index) => (
          <OutputCard key={index} output={output} labColor={labColor} />
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * Reusable Output Card
 */
export function OutputCard({ output, labColor }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F8FAFC] border border-[#E5E7EB]">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${labColor}15` }}>
        <FileText className="w-4 h-4" style={{ color: labColor }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#111827] truncate">{output.title}</p>
        <p className="text-xs text-[#1E3A8A]/50">{output.type}</p>
      </div>
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0">
        <Download className="w-4 h-4 text-[#1E3A8A]/50" />
      </Button>
    </div>
  );
}
